/**
 * Standard API error envelope (mirrors backend `AppError` + mobile error shape).
 */
export class ApiException extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
    public readonly details: unknown = null,
  ) {
    super(message);
    this.name = 'ApiException';
  }

  static fromResponse(res: Response, body: unknown): ApiException {
    if (typeof body === 'object' && body !== null && 'error' in body) {
      const err = body as {
        error: { code: string; message: string; details?: unknown };
      };
      return new ApiException(
        err.error.code,
        err.error.message,
        res.status,
        err.error.details ?? null,
      );
    }
    return new ApiException(
      'UNKNOWN_ERROR',
      `Request failed with status ${res.status}`,
      res.status,
    );
  }

  static network(cause?: unknown): ApiException {
    const reason =
      cause instanceof Error ? `${cause.name}: ${cause.message}` : String(cause ?? 'unknown');
    return new ApiException('NETWORK_ERROR', `Network request failed (${reason}).`, 0, { cause });
  }
}

/** Classify an unknown error into an ApiException or re-throw. */
export function classifyError(err: unknown): ApiException {
  if (err instanceof ApiException) return err;
  if (err instanceof TypeError) return ApiException.network(err);
  return new ApiException('UNKNOWN_ERROR', String(err), 0);
}
